<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { mdiEyeOutline as tablePrimaryIcon } from "@mdi/js"

  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import Table, {
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { spaNavigate } from "../../../../../../../../_shared/_logic/navigation"
  import { fetchWorkOrderTable } from "../../../../../../../../api/kehrblatt/work-orders"
  import {
    buildWorkOrderRows,
    columns,
    type WorkOrderRow,
  } from "../../../../_shared/_logic/work-order-table-model"
  import {
    executeSingleConfirmAppointment,
    executeSingleRescheduleAppointment,
  } from "../../../../_shared/_logic/single-appointment-actions"
  import {
    buildWorkOrderFilterRefreshKey,
    type WorkOrderFilterValue,
  } from "../../_shared/_logic/work-order-filters"
  import {
    coerceEntityList,
    indexById,
    mergeById,
    rebuildRelevantScheduleEvents,
  } from "../../_shared/_logic/entities"
  import { buildWorkOrderTableQuery } from "../../_shared/_logic/query"
  import { isActiveContactOrder } from "../../../../../../../../_shared/_logic/outreach-presentation"
  import AppointmentConfirmDialog from "../../../../_shared/_modules/appointment-confirm-dialog"
  import AppointmentRescheduleDialog from "../../../../_shared/_modules/appointment-reschedule-dialog"
  import WorkOrderTableCell from "../../../../_shared/_modules/work-order-table-cell"
  import WorkOrderRowActions from "./_actions/WorkOrderRowActions.actions"
  import WorkOrderBatchActionBar from "./_modules/work-order-batch-action-bar"
  import {
    invalidateWorkOrdersOverview,
    workOrdersTableInvalidationTarget,
  } from "../../_shared/_logic/invalidation"

  interface Props {
    organization: OrganizationFrame
    filters: WorkOrderFilterValue
    orderTypes: OrderType[]
    scheduleEvents: ScheduleEvent[]
    contactOrders: ContactOrder[]
    relevantScheduleEventByWorkOrderId: Record<string, ScheduleEvent>
    properties: Property[]
    customers: Customer[]
    activeContactOrderByWorkOrderId: Record<string, ContactOrder>
    viewEmployees: { id: string; label: string }[]
    planNowEmployees: { id: string; label: string }[]
    workingHoursByUser: Record<string, any>
    allEmployeeIds: string[]
    planningSettings: PlanningSettings | null
    loading: boolean
    workOrders: WorkOrder[]
    onTotalChange?: (total: number) => void
    onProjectionEntitiesLoaded?: (payload: {
      properties?: Property[] | null
      customers?: Customer[] | null
      contactPoints?: ContactPoint[] | null
      orderTypes?: OrderType[] | null
    }) => void
    canEdit?: boolean
    canPlanAppointments?: boolean
    trialRestrictionsActive?: boolean | null
  }

  const PAGE_SIZE = 80
  const DEFAULT_SORT = {
    columnId: "statusLabel",
    key: "active_appointment",
    dir: "desc" as const,
  }
  let {
    organization,
    filters,
    orderTypes,
    scheduleEvents,
    contactOrders,
    relevantScheduleEventByWorkOrderId,
    properties,
    customers,
    activeContactOrderByWorkOrderId,
    viewEmployees,
    planNowEmployees,
    workingHoursByUser,
    allEmployeeIds,
    planningSettings,
    loading,
    workOrders,
    onTotalChange,
    onProjectionEntitiesLoaded,
    canEdit = true,
    canPlanAppointments = false,
    trialRestrictionsActive = false,
  }: Props = $props()

  let actionLoading = $state(false)
  let confirmDialogTarget = $state<{ event: ScheduleEvent; workOrder: WorkOrder } | null>(null)
  let confirmingAppointment = $state(false)
  let confirmDialogError = $state<string | null>(null)
  let rescheduleDialogTarget = $state<{ event: ScheduleEvent; workOrder: WorkOrder } | null>(null)
  let reschedulingAppointment = $state(false)
  let rescheduleDialogError = $state<string | null>(null)
  let loadedProperties = $state<Property[]>([])
  let loadedCustomers = $state<Customer[]>([])
  let loadedContactPoints = $state<ContactPoint[]>([])
  let loadedWorkOrders = $state<WorkOrder[]>([])
  let loadedScheduleEvents = $state<ScheduleEvent[]>([])
  let removedScheduleEventIds = $state<string[]>([])
  let loadedContactOrders = $state<ContactOrder[]>([])
  let loadedOrderTypes = $state<OrderType[]>([])
  let latestLoadRequest = 0
  const tableInvalidationTargets = $derived(
    organization?.id ? [workOrdersTableInvalidationTarget(organization.id)] : [],
  )
  const filterRefreshKey = $derived(buildWorkOrderFilterRefreshKey(filters))

  const effectiveProperties = $derived(mergeById(properties ?? [], loadedProperties))
  const effectiveCustomers = $derived(mergeById(customers ?? [], loadedCustomers))
  const effectiveContactPoints = $derived(loadedContactPoints)
  const removedScheduleEventIdSet = $derived(new Set(removedScheduleEventIds))
  const effectiveWorkOrders = $derived(mergeById(workOrders ?? [], loadedWorkOrders))
  const effectiveScheduleEvents = $derived(
    mergeById(scheduleEvents ?? [], loadedScheduleEvents).filter(
      (entry) => !removedScheduleEventIdSet.has(entry.id),
    ),
  )
  const effectiveContactOrders = $derived(mergeById(contactOrders ?? [], loadedContactOrders))
  const effectiveActiveContactOrderByWorkOrderId = $derived.by(() => {
    const entries = effectiveContactOrders
      .map((order) => {
        const refs =
          (order as { product_refs?: Record<string, unknown> | null }).product_refs &&
          typeof (order as { product_refs?: Record<string, unknown> | null }).product_refs ===
            "object"
            ? (order as { product_refs?: Record<string, unknown> }).product_refs
            : null
        const workOrderId =
          typeof order.work_order_id === "string" && order.work_order_id
            ? order.work_order_id
            : typeof refs?.work_order_id === "string"
              ? refs.work_order_id
              : null

        if (!workOrderId || !isActiveContactOrder(order)) return null

        return [workOrderId, order] as const
      })
      .filter((entry): entry is readonly [string, ContactOrder] => Boolean(entry))

    return {
      ...activeContactOrderByWorkOrderId,
      ...Object.fromEntries(entries),
    }
  })
  const effectiveRelevantScheduleEventByWorkOrderId = $derived.by(() => {
    const explicitEvents = Object.entries(relevantScheduleEventByWorkOrderId ?? {}).filter(
      ([, event]) => event?.id && !removedScheduleEventIdSet.has(event.id),
    )

    return {
      ...Object.fromEntries(explicitEvents),
      ...rebuildRelevantScheduleEvents(effectiveScheduleEvents),
    }
  })
  const scopedCanEdit = $derived(Boolean(canEdit))
  const canEditWorkOrderRow = (row: WorkOrderRow): boolean =>
    Boolean(scopedCanEdit && row.workOrder.can_manage)
  const canSelectWorkOrderRow = (row: WorkOrderRow): boolean =>
    Boolean(canEditWorkOrderRow(row) && !row.workOrder.archived_at)
  const canPlanWorkOrderRow = (row: WorkOrderRow): boolean =>
    Boolean(canPlanAppointments && canEditWorkOrderRow(row))

  const invalidateOverview = async () => {
    if (!organization?.id) return

    await invalidateWorkOrdersOverview(organization.id)
  }

  const currentProjectionEntities = () => {
    const removedIds = new Set(removedScheduleEventIds)
    const currentScheduleEvents = mergeById(scheduleEvents ?? [], loadedScheduleEvents).filter(
      (entry) => !removedIds.has(entry.id),
    )

    return {
      properties: mergeById(properties ?? [], loadedProperties),
      customers: mergeById(customers ?? [], loadedCustomers),
      contactPoints: loadedContactPoints,
      workOrders: mergeById(workOrders ?? [], loadedWorkOrders),
      scheduleEvents: currentScheduleEvents,
      removedScheduleEventIds,
      contactOrders: mergeById(contactOrders ?? [], loadedContactOrders),
      orderTypes: mergeById(orderTypes ?? [], loadedOrderTypes),
    }
  }

  const loadWorkOrderRows: TableGetRows<TableFilterValues> = async (params) => {
    const request = ++latestLoadRequest
    const query = buildWorkOrderTableQuery({
      organizationId: organization?.id,
      values: {
        searchQuery: filters.searchQuery,
        orderTypeIds: filters.orderTypeIds ?? [],
        appointmentDateRange: filters.appointmentDateRange,
        employeeIds: filters.employeeIds ?? [],
        workOrderStates: filters.workOrderStates?.map((entry) => `${entry}`) ?? [],
        actionRequiredState: filters.actionRequiredState,
        dueDateRange: filters.dueDateRange,
        archiveScope: filters.archiveScope,
      },
      currentFilters: filters,
      offset: params.offset,
      limit: params.limit,
    })

    if (!organization?.id || !query) {
      return { rows: [], total: 0 }
    }

    const sort = (
      params as {
        sort?: {
          key?: string | null
          dir?: "asc" | "desc" | null
        } | null
      }
    ).sort
    try {
      const response = await fetchWorkOrderTable({
        ...query,
        sortBy: sort?.key ?? null,
        sortDir: sort?.dir ?? null,
      })

      if (request !== latestLoadRequest) {
        return { rows: [], total: 0 }
      }

      const currentProjection = currentProjectionEntities()
      const responseScheduleEvents = response.schedule_events ?? []
      const responseScheduleEventIds = new Set(
        responseScheduleEvents.map((entry) => entry.id).filter(Boolean),
      )

      loadedWorkOrders = mergeById(
        (params.offset ?? 0) === 0 ? [] : currentProjection.workOrders,
        response.items ?? [],
      )
      loadedProperties = mergeById(currentProjection.properties, response.properties ?? [])
      loadedCustomers = mergeById(currentProjection.customers, response.customers ?? [])
      loadedContactPoints = mergeById<ContactPoint>(
        currentProjection.contactPoints,
        (response.contact_points ?? []) as ContactPoint[] | Record<string, unknown>,
      )
      removedScheduleEventIds = currentProjection.removedScheduleEventIds.filter(
        (eventId) => !responseScheduleEventIds.has(eventId),
      )
      loadedScheduleEvents = mergeById(
        currentProjection.scheduleEvents,
        responseScheduleEvents,
      ).filter((entry) => !removedScheduleEventIds.includes(entry.id))
      loadedContactOrders = mergeById(
        currentProjection.contactOrders,
        response.contact_orders ?? [],
      )
      loadedOrderTypes = mergeById(currentProjection.orderTypes, response.order_types ?? [])
      onProjectionEntitiesLoaded?.({
        properties: coerceEntityList<Property>(response.properties ?? []),
        customers: coerceEntityList<Customer>(response.customers ?? []),
        contactPoints: coerceEntityList<ContactPoint>(
          (response.contact_points ?? []) as ContactPoint[] | Record<string, unknown>,
        ),
        orderTypes: coerceEntityList<OrderType>(response.order_types ?? []),
      })

      const rows = buildWorkOrderRows({
        workOrders: response.items ?? [],
        propertyById: indexById(loadedProperties),
        customersById: indexById(loadedCustomers),
        contactPoints: loadedContactPoints,
        orderTypes: loadedOrderTypes,
        contactOrders: loadedContactOrders,
        relevantScheduleEventByWorkOrderId: {
          ...Object.fromEntries(
            Object.entries(relevantScheduleEventByWorkOrderId ?? {}).filter(
              ([, event]) => event?.id && !removedScheduleEventIds.includes(event.id),
            ),
          ),
          ...rebuildRelevantScheduleEvents(loadedScheduleEvents),
        },
        scheduleEvents: loadedScheduleEvents,
        notificationCounts: response.notification_counts ?? [],
        dueLetterDeliveryStepsByWorkOrderId:
          response.due_letter_delivery_steps_by_work_order_id ?? {},
        communicationPresentationsByScheduleEventId:
          response.schedule_event_communication_presentations ?? {},
        employees: viewEmployees,
      })

      return {
        rows,
        total: Number.isFinite(response.total) ? response.total : (response.items ?? []).length,
      }
    } catch (err) {
      if (request === latestLoadRequest) {
        console.error("work_orders_fetch_failed", err)
      }

      throw err
    }
  }

  const openWorkOrder = (row: any) => {
    if (!organization?.id) return

    const orgSlug = organization?.slug ?? null

    if (!orgSlug) return

    spaNavigate(`/organizations/${orgSlug}/work-orders/${row.id}/communications?tab=1`, {
      noSearchParam: false,
    })
  }

  const handleScheduleEventUpdated = async (
    updated?: ScheduleEvent | null,
    removedId?: string | null,
  ) => {
    if (removedId) {
      removedScheduleEventIds = Array.from(new Set([...removedScheduleEventIds, removedId]))
      loadedScheduleEvents = loadedScheduleEvents.filter((entry) => entry.id !== removedId)

      return
    }

    if (!updated || !updated.id) {
      await invalidateOverview()

      return
    }

    removedScheduleEventIds = removedScheduleEventIds.filter((entry) => entry !== updated.id)
    loadedScheduleEvents = mergeById(currentProjectionEntities().scheduleEvents, [updated])
  }

  const handleScheduleEventsUpdated = async (updatedEvents: ScheduleEvent[]) => {
    loadedScheduleEvents = mergeById(loadedScheduleEvents, updatedEvents)
  }

  const handleChanged = async () => {
    await invalidateOverview()
  }

  const openConfirmAppointmentDialog = (event: ScheduleEvent, targetWorkOrder: WorkOrder) => {
    if (!event?.id || loading || actionLoading || confirmingAppointment) return

    confirmDialogError = null
    confirmDialogTarget = { event, workOrder: targetWorkOrder }
  }

  const closeConfirmAppointmentDialog = () => {
    if (confirmingAppointment) return

    confirmDialogTarget = null
    confirmDialogError = null
  }

  const submitConfirmAppointment = async ({
    sendConfirmationEmail,
  }: {
    sendConfirmationEmail: boolean
  }) => {
    const target = confirmDialogTarget

    if (!target?.event?.id || confirmingAppointment) return

    confirmingAppointment = true
    confirmDialogError = null
    try {
      const response = await executeSingleConfirmAppointment({
        eventId: target.event.id,
        sendConfirmationEmail,
      })
      await handleScheduleEventUpdated(response.event, null)
      await invalidateOverview()
      confirmDialogTarget = null
    } catch (err) {
      console.error("schedule_event_single_confirm_failed", err)
      const message =
        (err as { message?: string })?.message?.trim() || "Termin konnte nicht bestätigt werden."
      confirmDialogError = message
      newNotification({ class: "error", html: message })
    } finally {
      confirmingAppointment = false
    }
  }

  const openRescheduleAppointmentDialog = (event: ScheduleEvent, targetWorkOrder: WorkOrder) => {
    if (!event?.id || loading || actionLoading || reschedulingAppointment) return

    rescheduleDialogError = null
    rescheduleDialogTarget = { event, workOrder: targetWorkOrder }
  }

  const closeRescheduleAppointmentDialog = () => {
    if (reschedulingAppointment) return

    rescheduleDialogTarget = null
    rescheduleDialogError = null
  }

  const submitRescheduleAppointment = async ({
    updatePayload,
    sendConfirmationEmail,
  }: {
    updatePayload: ScheduleEventUpdatePayload
    sendConfirmationEmail: boolean
  }) => {
    const target = rescheduleDialogTarget

    if (!target?.event?.id || reschedulingAppointment) return

    reschedulingAppointment = true
    rescheduleDialogError = null
    try {
      const result = await executeSingleRescheduleAppointment({
        eventId: target.event.id,
        updatePayload,
        sendConfirmationEmail,
      })

      if (result.status === "canceled") return

      await handleScheduleEventUpdated(result.value.created_event, null)
      await invalidateOverview()
      rescheduleDialogTarget = null
    } catch (err) {
      console.error("schedule_event_single_reschedule_failed", err)
      const message =
        (err as { message?: string })?.message?.trim() || "Termin konnte nicht verschoben werden."
      rescheduleDialogError = message
      newNotification({ class: "error", html: message })
    } finally {
      reschedulingAppointment = false
    }
  }

  async function runTableAction<T>(fn: () => Promise<T> | T) {
    if (actionLoading) return undefined

    actionLoading = true
    try {
      return await fn()
    } finally {
      actionLoading = false
    }
  }
</script>

<Table
  rowActions={{
    resolve: (row, rowIndex) =>
      buildTableRowActionSet(
        [
          {
            id: "open-record",
            label: "Auftrag öffnen",
            icon: tablePrimaryIcon,
            onClick: () => openWorkOrder(row),
          },
          ...(((row) => {
            const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

            const workOrderRow = row as WorkOrderRow

            actions.push(
              ...WorkOrderRowActions({
                row: workOrderRow,
                organizationId: organization.id,
                workOrder: workOrderRow.workOrder,
                scopedCanEdit: canEditWorkOrderRow(workOrderRow),
                canPlanAppointments: canPlanWorkOrderRow(workOrderRow),
                loading: loading,
                actionLoading: actionLoading,
                relevantScheduleEvent: effectiveRelevantScheduleEventByWorkOrderId[row.id],
                scheduleEvents: effectiveScheduleEvents,
                effectiveProperties: effectiveProperties,
                effectiveCustomers: effectiveCustomers,
                effectiveContactPoints: effectiveContactPoints,
                contactOrders: effectiveContactOrders,
                employees: planNowEmployees,
                planningSettings: planningSettings,
                workingHoursByUser: workingHoursByUser,
                allEmployeeIds: allEmployeeIds,
                runTableAction: runTableAction,
                onChanged: handleChanged,
                onCreated: async () => {
                  await invalidateOverview()
                  const orgSlug = organization?.slug ?? null

                  if (!orgSlug) return

                  spaNavigate(`/organizations/${orgSlug}/work-orders/${row.id}/communications`, {
                    noSearchParam: false,
                  })
                },
                onConfirmAppointment: openConfirmAppointmentDialog,
                onRescheduleAppointment: openRescheduleAppointmentDialog,
                onScheduleEventUpdated: handleScheduleEventUpdated,
              }),
            )

            return actions
          })(row) ?? []),
        ],
        { primaryId: "open-record", inlineIds: ["action-0", "action-1", "action-3"] },
      ),
  }}
  {columns}
  getRows={loadWorkOrderRows}
  showFilters={false}
  pageSize={PAGE_SIZE}
  defaultSort={DEFAULT_SORT}
  refreshKey={filterRefreshKey}
  invalidationTargets={tableInvalidationTargets}
  urlStateId={organization?.id
    ? `kehrblatt-work-orders-${organization.id}`
    : "kehrblatt-work-orders"}
  theme="light"
  mode="table-scroll"
  showActions={true}
  actionsHeaderLabel="Aktionen"
  enableSelection={scopedCanEdit}
  getRowState={(row) => ({ selectable: canSelectWorkOrderRow(row as WorkOrderRow) })}
  showBatchActionCount={false}
  onRowsLoaded={(result) => {
    onTotalChange?.(result.total ?? 0)
  }}
>
  {#snippet tableCell({ row, column })}
    {@const workOrderRow = row as WorkOrderRow}
    <WorkOrderTableCell row={workOrderRow} {column} />
  {/snippet}
  {#snippet empty()}
    <div class="empty-state">
      <p style="color: var(--text-invers-100)">
        Keine Aufträge gefunden. Prüfe ggf. den Filter oder lege einen Auftrag an.
      </p>
    </div>
  {/snippet}

  {#snippet batchActions({ selectedRows, selectedCount, clearSelection })}
    <WorkOrderBatchActionBar
      organizationId={organization.id}
      {selectedRows}
      {selectedCount}
      {actionLoading}
      {scopedCanEdit}
      {canPlanAppointments}
      relevantScheduleEventByWorkOrderId={effectiveRelevantScheduleEventByWorkOrderId}
      activeContactOrderByWorkOrderId={effectiveActiveContactOrderByWorkOrderId}
      properties={effectiveProperties}
      customers={effectiveCustomers}
      contactPoints={effectiveContactPoints}
      contactOrders={effectiveContactOrders}
      employees={planNowEmployees}
      {workingHoursByUser}
      {planningSettings}
      {trialRestrictionsActive}
      {runTableAction}
      {clearSelection}
      onChanged={handleChanged}
      onScheduleEventsUpdated={handleScheduleEventsUpdated}
    />
  {/snippet}
</Table>

{#if confirmDialogTarget}
  <AppointmentConfirmDialog
    pending={confirmingAppointment}
    error={confirmDialogError}
    onConfirm={submitConfirmAppointment}
    onClose={closeConfirmAppointmentDialog}
  />
{/if}

{#if rescheduleDialogTarget}
  <AppointmentRescheduleDialog
    event={rescheduleDialogTarget.event}
    workOrder={rescheduleDialogTarget.workOrder}
    employees={planNowEmployees}
    scheduleEvents={effectiveScheduleEvents}
    {workingHoursByUser}
    pending={reschedulingAppointment}
    error={rescheduleDialogError}
    onReschedule={submitRescheduleAppointment}
    onClose={closeRescheduleAppointmentDialog}
  />
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .empty-state {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0.6rem 0;

    p {
      margin: 0;
      color: color-mix(in srgb, var(--text-100) 70%, transparent);
    }
  }
</style>
